import { CircularProgress, Grid, Paper } from '@mui/material';
import { ColumnSizingState } from '@tanstack/react-table';
import { Formik, FormikProps } from 'formik';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { errorMessage } from 'store/actions/messageActions';
import store from 'store/store';
import { Container, GetSearchContainersParams } from '../../global/adapters/crpAPI';
import { DataGrid } from '../../global/components';
import { Drawer } from '../../global/components/Drawer';
import EmptyState from '../../global/components/EmptyState/EmptyState';
import { useLocalTableValues } from '../../global/hooks';
import { useSearchContainers } from '../adapters/search-containers/queries';
import StepperSearchContainers from '../components/search-containers/StepperSearchContainers';
import ContainerDetailDrawer from '../components/shared/ContainerDetailDrawer';
import { baseShippingLineColumnsWithStatus } from '../constants/shared/shippingLineColumns';
import { useGetContainersByDashboardMetric } from '../adapters/dashboard/queries';
import { DashboardMetricType } from 'modules/transport-operator/adapters/dashboard/queries';
import { isFromDashboard } from 'modules/global/helpers/searchParamsHelper';

type Props = React.ComponentPropsWithoutRef<'div'> & {};

const initialFields: GetSearchContainersParams = {
  vesselName: '',
  imoNumber: '',
  stayNumber: '',
  releaseRightStatus: undefined,
  isArchived: false,
  billOfLadingNumber: '',
  equipmentNumber: '',
};

const SearchContainers: React.FC<Props> = () => {
  const { t } = useTranslation(['changeVoyage', 'common', 'validation']);

  const formRef = useRef<FormikProps<GetSearchContainersParams>>();
  const [searchParams, setSearchParams] = React.useState<GetSearchContainersParams>(initialFields);

  const [displayGrid, setDisplayGrid] = React.useState(false);
  const [searchPerformed, setSearchPerformed] = React.useState(false);
  const [showDetail, setShowDetail] = React.useState<{
    open: boolean;
    data?: Container;
  }>({
    open: false,
    data: undefined,
  });

  const [columnSizing, setColumnSizing] = useLocalTableValues<ColumnSizingState>(
    'column-sizing-update-voyage',
    {}
  );

  const containersByDashboardMetric = useGetContainersByDashboardMetric(
    DashboardMetricType.CommercialReleases,
    isFromDashboard()
  );

  const searchContainers = useSearchContainers(searchParams);

  const { data, isLoading, isFetching, isError } = searchPerformed
    ? searchContainers
    : containersByDashboardMetric;

  const handleOnSubmit = (values: GetSearchContainersParams) => {
    setSearchParams(values);
    setSearchPerformed(true);
  };

  const resetSearch = () => {
    setDisplayGrid(false);
    setSearchPerformed(false);
  };

  React.useEffect(() => {
    isError && store.dispatch(errorMessage(t('validation:errorGetContainers')));
  }, [isError, t]);

  React.useEffect(() => {
    if (!isFetching && data) {
      setDisplayGrid(true);
    }
  }, [isFetching, data]);

  const handleCellClick = (myRowData: Container) => {
    setShowDetail({ open: true, data: myRowData });
  };

  return (
    <Grid container spacing={2} style={{ margin: '20px 0 0 5px' }}>
      <Grid size={{ xs: 4, sm: 3 }}>
        <Formik
          initialValues={initialFields}
          innerRef={formRef as React.Ref<FormikProps<GetSearchContainersParams>>}
          onSubmit={handleOnSubmit}
        >
          {({ submitForm, resetForm, values }: FormikProps<GetSearchContainersParams>) => {
            return (
              <StepperSearchContainers
                submitForm={submitForm}
                values={values}
                isFetching={isFetching}
                resetForm={resetForm}
                onResetSearch={resetSearch}
              />
            );
          }}
        </Formik>
      </Grid>
      <Grid size={{ xs: 8, sm: 9 }}>
        {isFetching ? (
          <CircularProgress />
        ) : isError ? (
          <EmptyState title={t('validation:errorGetContainers')} />
        ) : displayGrid && data ? (
          <React.Fragment>
            <Paper>
              <DataGrid.Client
                columns={baseShippingLineColumnsWithStatus}
                columnSizing={columnSizing}
                data={data}
                isLoading={isLoading}
                onColumnSizingChange={setColumnSizing}
                onCellClick={handleCellClick}
                rowSelection={{}}
              />
            </Paper>
            <Drawer setShowDrawer={setShowDetail} showDrawer={showDetail.open}>
              {showDetail.data !== undefined && (
                <ContainerDetailDrawer containerId={showDetail.data.releaseId} />
              )}
            </Drawer>
          </React.Fragment>
        ) : (
          <EmptyState title={t('emptyState.title')} subTitle={t('emptyState.subTitle')} />
        )}
      </Grid>
    </Grid>
  );
};

export default SearchContainers;
